import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const API_URL = 'http://localhost:8081/api/offers';

interface Offer {
  id?: number;
  title: string;
  type: 'Stage' | 'Emploi';
  description: string;
  location: string;
  date: string;
}

const Admin = () => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [form, setForm] = useState<Offer>({ title: '', type: 'Stage', description: '', location: '', date: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'ajouter' | 'liste'>('ajouter');

  useEffect(() => {
    if (isLoggedIn) {
      fetch(API_URL)
        .then(res => res.json())
        .then(setOffers)
        .catch(() => setError("Erreur lors du chargement des offres"));
    }
  }, [isLoggedIn]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!login || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    try {
      const response = await fetch('http://localhost:8081/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: login, password })
      });
      if (response.ok) {
        setIsLoggedIn(true);
        setError('');
      } else {
        const message = await response.text();
        setError(message || 'Erreur de connexion');
      }
    } catch (err) {
      setError("Erreur lors de l'accès au serveur");
    }
  };

  // Ajout ou modification d'offre
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      if (editingId) {
        // Modification
        const response = await fetch(`${API_URL}/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
        if (!response.ok) throw new Error('Erreur lors de la modification');
        setOffers(offers.map(o => o.id === editingId ? { ...form, id: editingId } : o));
        setEditingId(null);
        setForm({ title: '', type: 'Stage', description: '', location: '', date: '' });
        Swal.fire({
          icon: 'success',
          title: 'Offre modifiée !',
          text: 'L\'offre a été modifiée avec succès.',
          confirmButtonColor: '#2563eb',
        });
      } else {
        // Ajout
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
        if (!response.ok) throw new Error('Erreur lors de l\'ajout');
        const newOffer = await response.json();
        setOffers([newOffer, ...offers]);
        setForm({ title: '', type: 'Stage', description: '', location: '', date: '' });
        Swal.fire({
          icon: 'success',
          title: 'Offre ajoutée !',
          text: 'L\'offre a été ajoutée avec succès.',
          confirmButtonColor: '#22c55e',
        });
      }
    } catch (err: any) {
      setFormError(err.message || 'Erreur');
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.message || 'Erreur',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  // Pré-remplir pour modification
  const handleEdit = (offer: Offer) => {
    setForm(offer);
    setEditingId(offer.id!);
  };

  // Suppression
  const handleDelete = async (id?: number) => {
    if (!id) return;
    const result = await Swal.fire({
      title: 'Supprimer cette offre ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/${id}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Erreur lors de la suppression');
        setOffers(offers.filter(o => o.id !== id));
        Swal.fire({
          icon: 'success',
          title: 'Supprimé !',
          text: 'L\'offre a été supprimée.',
          confirmButtonColor: '#22c55e',
        });
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: err.message || 'Erreur',
          confirmButtonColor: '#ef4444',
        });
      }
    }
  };

  // Gestion des champs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  if (!isLoggedIn) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-red-100 via-white to-green-100 py-16">
          <form onSubmit={handleLogin} className="bg-white/90 p-10 rounded-3xl shadow-2xl border-2 border-red-200 w-full max-w-md space-y-6">
            <h2 className="text-3xl font-extrabold text-green-700 mb-6 text-center tracking-tight drop-shadow">Connexion Admin</h2>
            <div>
              <label className="block text-black mb-2 font-semibold">Login</label>
              <input
                type="text"
                value={login}
                onChange={e => setLogin(e.target.value)}
                className="w-full border-2 border-green-200 rounded-xl px-5 py-3 bg-white focus:border-green-400 focus:ring-2 focus:ring-green-100 text-green-700 placeholder-gray-400 transition shadow-sm"
                placeholder="Nom d'utilisateur"
                required
              />
            </div>
            <div>
              <label className="block text-black mb-2 font-semibold">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border-2 border-green-200 rounded-xl px-5 py-3 bg-white focus:border-green-400 focus:ring-2 focus:ring-green-100 text-green-700 placeholder-gray-400 transition shadow-sm"
                placeholder="Mot de passe"
                required
              />
            </div>
            {error && <p className="text-red-500 text-center">{error}</p>}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-green-400 via-green-500 to-red-400 text-white font-bold py-3 rounded-xl shadow-md hover:scale-105 hover:shadow-lg transition-all duration-200 text-lg mt-2"
            >
              Se connecter
            </button>
          </form>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="w-full flex justify-center py-6 mb-8">
        <div className="flex gap-6 bg-white/80 rounded-3xl shadow-2xl px-8 py-2 border-2 border-red-200 backdrop-blur-md">
          <button
            className={`px-5 py-2 rounded-full font-semibold transition text-green-700 hover:bg-green-100 hover:text-green-900 ${activeTab === 'ajouter' ? 'bg-green-100 ring-2 ring-green-300' : ''}`}
            onClick={() => setActiveTab('ajouter')}
          >Ajouter une offre</button>
          <button
            className={`px-5 py-2 rounded-full font-semibold transition text-red-700 hover:bg-red-100 hover:text-red-900 ${activeTab === 'liste' ? 'bg-red-100 ring-2 ring-red-300' : ''}`}
            onClick={() => setActiveTab('liste')}
          >Liste des offres</button>
          <a
            href="/admin/candidatures"
            className="px-5 py-2 rounded-full font-semibold transition text-blue-700 hover:bg-blue-100 hover:text-blue-900"
          >Candidatures</a>
        </div>
      </div>
      <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-red-100 via-white to-green-100 py-16">
        {activeTab === 'ajouter' && (
          <div className="bg-white/90 p-10 rounded-3xl shadow-2xl border-2 border-green-200 w-full max-w-2xl mb-8 backdrop-blur-md">
            <h2 className="text-3xl font-extrabold text-green-700 mb-6 text-center tracking-tight drop-shadow">{editingId ? 'Modifier' : 'Ajouter'} une offre</h2>
            <form onSubmit={handleSubmit} className="space-y-6 text-left">
              <div>
                <label className="block text-green-700 mb-2 font-semibold">Titre de l'offre</label>
                <input type="text" name="title" value={form.title} onChange={handleChange} className="w-full border-2 border-green-200 rounded-xl px-5 py-3 bg-white/80 focus:border-green-400 focus:ring-2 focus:ring-green-100 text-green-700 placeholder-gray-400 transition shadow-sm" required />
              </div>
              <div>
                <label className="block text-green-700 mb-2 font-semibold">Type de contrat</label>
                <select name="type" value={form.type} onChange={handleChange} className="w-full border-2 border-green-200 rounded-xl px-5 py-3 bg-white/80 focus:border-green-400 focus:ring-2 focus:ring-green-100 text-green-700 placeholder-gray-400 transition shadow-sm">
                  <option value="Stage">Stage</option>
                  <option value="Emploi">Emploi</option>
                </select>
              </div>
              <div>
                <label className="block text-green-700 mb-2 font-semibold">Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} className="w-full border-2 border-green-200 rounded-xl px-5 py-3 bg-white/80 focus:border-green-400 focus:ring-2 focus:ring-green-100 text-green-700 placeholder-gray-400 transition shadow-sm" rows={3} required />
              </div>
              <div>
                <label className="block text-green-700 mb-2 font-semibold">Lieu</label>
                <input type="text" name="location" value={form.location} onChange={handleChange} className="w-full border-2 border-green-200 rounded-xl px-5 py-3 bg-white/80 focus:border-green-400 focus:ring-2 focus:ring-green-100 text-green-700 placeholder-gray-400 transition shadow-sm" required />
              </div>
              <div>
                <label className="block text-green-700 mb-2 font-semibold">Date</label>
                <input type="date" name="date" value={form.date} onChange={handleChange} className="w-full border-2 border-green-200 rounded-xl px-5 py-3 bg-white/80 focus:border-green-400 focus:ring-2 focus:ring-green-100 text-green-700 placeholder-gray-400 transition shadow-sm" required />
              </div>
              {formError && <p className="text-red-500 text-center">{formError}</p>}
              <button type="submit" className="w-full bg-gradient-to-r from-green-400 via-green-500 to-red-400 text-white font-bold py-3 rounded-xl shadow-md hover:scale-105 hover:shadow-lg transition-all duration-200 text-lg mt-2">
                {editingId ? 'Modifier' : 'Ajouter'} l'offre
              </button>
              {editingId && (
                <button type="button" onClick={() => { setForm({ title: '', type: 'Stage', description: '', location: '', date: '' }); setEditingId(null); }} className="w-full mt-2 bg-gray-200 text-gray-700 font-bold py-3 rounded-lg shadow hover:bg-gray-300 transition-all duration-200 text-lg">
                  Annuler
                </button>
              )}
            </form>
          </div>
        )}
        {activeTab === 'liste' && (
          <div className="w-full max-w-2xl">
            <h3 className="text-xl font-bold mb-4 text-green-700">Liste des offres</h3>
            <ul className="space-y-4">
              {offers.length === 0 && <li className="text-gray-500">Aucune offre pour le moment.</li>}
              {offers.map((offer) => (
                <li key={offer.id} className="p-4 rounded-xl border-2 border-green-100 bg-white/80 text-left flex flex-col md:flex-row md:items-center md:justify-between gap-2 shadow">
                  <div>
                    <div className="font-semibold text-green-700">{offer.title}</div>
                    <div className="text-sm px-2 py-1 rounded bg-green-100 text-green-700 inline-block mr-2">{offer.type}</div>
                    <div className="text-gray-700 mb-1">{offer.description}</div>
                    <div className="text-sm text-gray-500">{offer.location} — {offer.date}</div>
                  </div>
                  <div className="flex gap-2 mt-2 md:mt-0">
                    <button onClick={() => handleEdit(offer)} className="bg-yellow-400 text-white px-4 py-2 rounded hover:bg-yellow-500 transition">Modifier</button>
                    <button onClick={() => handleDelete(offer.id)} className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition">Supprimer</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default Admin;